import { ONBOARDING_PATHS } from './paths';

export type OnboardingPageId = 'index' | 'verification' | 'wizard';

/** The account's onboarding position, as every routing decision reads it. */
export type OnboardingState = {
  /** Trial, non-builder, flag on — everyone onboarding applies to. */
  isEligible: boolean;
  isComplete: boolean;
  isVerified: boolean;
  hasAnswered: boolean;
};

export type OnboardingRedirectInput = OnboardingState & {
  page: OnboardingPageId;
};

/**
 * Where an `/onboarding/*` page should send the user, or `null` to stay.
 * Pure so the whole gate matrix is unit-tested once; pages render a
 * `SafeNavigate` off the answer.
 *
 * - Ineligible accounts and accounts that already finished onboarding in any
 *   workspace skip the flow entirely and land on home (`/` forwards there).
 * - The wizard owns answered users, so a return visit replays the recorded
 *   conversation instead of bouncing. Forward navigation is the page's own
 *   one-shot move.
 * - Everywhere else, users who already answered are done with `/onboarding` —
 *   home's checklist is their next step.
 * - Everyone else moves on to the wizard, verified or not: verification never
 *   blocks onboarding. The gate page stays usable for an unverified visitor,
 *   but nothing routes to it, and a verified visitor is moved along.
 */
export function selectOnboardingRedirect(input: OnboardingRedirectInput): string | null {
  const { page, isVerified, isEligible, isComplete, hasAnswered } = input;

  if (!isEligible || isComplete) {
    return '/';
  }
  if (page === 'wizard') {
    return null;
  }
  if (hasAnswered) {
    return '/';
  }
  if (page === 'verification' && !isVerified) {
    return null;
  }
  return ONBOARDING_PATHS.wizard;
}
